import {useContext, useEffect, useMemo, useState} from 'react'
import {
    Alert, Box, Button, Chip, LinearProgress, Stack, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {AuthContext} from '../../../contexts/auth'
import {AssistantApi} from '../../../services/AssistantApi.service'
import {AnalyzerApiService} from '../../../services/AnalyzerApi.service'
import {PriceResearchApiService} from '../../../services/PriceResearchApi.service'
import {
    AnalyzerMonthlyUsage, CoreMonthlyUsage, MonthlyUsagePeriod,
    MonthlyUsageUser, PriceUsageModule,
} from '../../../types/monthlyUsage'

type Status = 'loading' | 'ready' | 'error'
interface Source<T> {
    month: string
    status: Status
    data?: T
    authDenied?: boolean
}
interface ReportProps {
    rosterVersion?: string
}
interface PriceUsage {
    users: Map<string, {
        pesquisa_precos: number
        radar_fornecedores: number
        incomplete: boolean
    }>
    incomplete: boolean
}

const MONTH_NAMES = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
]
const UNITS = {
    consultivo: 'Créditos de consultas registrados, descontados os estornos.',
    documentos: 'Créditos de documentos registrados, descontados os estornos.',
    analise: 'Análises concluídas, agrupadas pela data de conclusão registrada. Análises antigas sem essa data não recebem um mês estimado.',
    pesquisa: 'Créditos de Pesquisa de Preços registrados no mês, descontados os estornos identificados. Preserva a unidade registrada, incluindo cobranças antigas por item.',
    radar: 'Créditos de Radar de Fornecedores registrados no mês, descontados os estornos identificados.',
    ata: 'Créditos de justificativas registrados, descontados os estornos.',
}

export function currentUsageMonth(date: Date = new Date()): string {
    const parts = new Intl.DateTimeFormat('en', {
        timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit',
    }).formatToParts(date)
    return `${parts.find((part) => part.type === 'year')?.value}-${parts.find((part) => part.type === 'month')?.value}`
}

function ColumnLabel({label, description}: {label: string; description: string}): JSX.Element {
    return (
        <Tooltip title={description} describeChild>
            <Box component="span" tabIndex={0} sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5}}>
                {label}<InfoOutlinedIcon sx={{fontSize: 15}} aria-hidden="true" />
            </Box>
        </Tooltip>
    )
}

function UsageValue({value, partial}: {value?: number; partial?: string}): JSX.Element {
    if (value === undefined || (value === 0 && partial)) {
        return partial ? (
            <Tooltip title={partial} describeChild>
                <span tabIndex={0} aria-label={`Indisponível. ${partial}`}>—</span>
            </Tooltip>
        ) : <span aria-label="Indisponível">—</span>
    }
    if (!partial) return <>{value.toLocaleString('pt-BR')}</>
    return (
        <Tooltip title={partial} describeChild>
            <Box component="span" tabIndex={0} aria-label={`${value}, parcial. ${partial}`}>
                {value.toLocaleString('pt-BR')}{' '}
                <Typography component="span" variant="caption" color="warning.main">parcial</Typography>
            </Box>
        </Tooltip>
    )
}

export default function MonthlyUsageReport({rosterVersion = ''}: ReportProps): JSX.Element | null {
    const {userInfo, logout} = useContext(AuthContext)
    if (!userInfo.org_admin || !userInfo.organization.guid) return null
    return (
        <MonthlyUsageReportContent
            key={`${userInfo.organization.guid}:${userInfo.guid}`}
            rosterVersion={rosterVersion}
            onLogin={logout}
        />
    )
}

function MonthlyUsageReportContent({rosterVersion, onLogin}: {
    rosterVersion: string
    onLogin: () => void
}): JSX.Element {
    const currentMonth = currentUsageMonth()
    const [month, setMonth] = useState(currentMonth)
    const [retry, setRetry] = useState(0)
    const [availableMonths, setAvailableMonths] = useState<string[]>([])
    const [users, setUsers] = useState<MonthlyUsageUser[]>([])
    const [core, setCore] = useState<Source<CoreMonthlyUsage>>({month, status: 'loading'})
    const [analyzer, setAnalyzer] = useState<Source<AnalyzerMonthlyUsage>>({month, status: 'loading'})
    const [price, setPrice] = useState<Source<PriceUsage>>({month, status: 'loading'})
    const clients = useMemo(() => ({
        core: new AssistantApi(), analyzer: new AnalyzerApiService(), price: new PriceResearchApiService(),
    }), [])

    useEffect(() => {
        const controller = new AbortController()
        let active = true
        setCore({month, status: 'loading'})
        setAnalyzer({month, status: 'loading'})
        setPrice({month, status: 'loading'})

        const isCurrent = (): boolean => active && !controller.signal.aborted
        function failure<T>(update: (source: Source<T>) => void, error: unknown): void {
            if (!isCurrent()) return
            const status = (error as {response?: {status?: number}})?.response?.status
            update({month, status: 'error', authDenied: status === 401 || status === 403})
        }
        function receivePeriod(data: MonthlyUsagePeriod): void {
            if (data.month !== month) throw new Error('Competência recebida diferente da solicitada.')
            setAvailableMonths((previous) => Array.from(new Set([...previous, ...data.available_months])))
        }

        async function loadPrice(data: CoreMonthlyUsage): Promise<void> {
            try {
                const entityUuids = Array.from(new Set(data.price_consumptions
                    .map((consumption) => consumption.entity_uuid)
                    .filter((value): value is string => value !== null)))
                const classifications = new Map<string, PriceUsageModule>()
                for (let offset = 0; offset < entityUuids.length; offset += 500) {
                    if (!isCurrent()) return
                    const result = await clients.price.classifyUsageEntities(
                        entityUuids.slice(offset, offset + 500), controller.signal
                    )
                    if (!isCurrent()) return
                    result.classifications.forEach(({entity_uuid, module}) => classifications.set(entity_uuid, module))
                }
                if (!isCurrent()) return
                const totals: PriceUsage = {users: new Map(), incomplete: false}
                data.price_consumptions.forEach(({user_guid, entity_uuid, value}) => {
                    const user = totals.users.get(user_guid) ?? {
                        pesquisa_precos: 0, radar_fornecedores: 0, incomplete: false,
                    }
                    const module = entity_uuid === null ? undefined : classifications.get(entity_uuid)
                    if (module) user[module] += value
                    else {
                        user.incomplete = true
                        totals.incomplete = true
                    }
                    totals.users.set(user_guid, user)
                })
                setPrice({month, status: 'ready', data: totals})
            } catch (error) {
                failure(setPrice, error)
            }
        }

        async function loadCore(): Promise<void> {
            try {
                const data = await clients.core.getOrganizationMonthlyUsage(month, controller.signal)
                if (!isCurrent()) return
                receivePeriod(data)
                setCore({month, status: 'ready', data})
                setUsers(data.users)
                await loadPrice(data)
            } catch (error) {
                failure(setCore, error)
                failure(setPrice, error)
            }
        }
        async function loadAnalyzer(): Promise<void> {
            try {
                const data = await clients.analyzer.getMonthlyUsage(month, controller.signal)
                if (!isCurrent()) return
                receivePeriod(data)
                setAnalyzer({month, status: 'ready', data})
            } catch (error) {
                failure(setAnalyzer, error)
            }
        }

        void loadCore()
        void loadAnalyzer()

        return () => {
            active = false
            controller.abort()
        }
    }, [clients, month, retry, rosterVersion])

    const status = (source: {month: string; status: Status}): Status => source.month === month ? source.status : 'loading'
    const sources = [core, analyzer, price]
    const loading = sources.some((source) => status(source) === 'loading')
    const failed = sources.some((source) => status(source) === 'error')
    const authDenied = sources.some((source) => status(source) === 'error' && source.authDenied)
    const coreData = status(core) === 'ready' ? core.data : undefined
    const analyzerData = status(analyzer) === 'ready' ? analyzer.data : undefined
    const priceData = status(price) === 'ready' ? price.data : undefined
    const coreUsers = new Map(coreData?.users.map((user) => [user.user_guid, user]))
    const analyzerUsers = new Map(analyzerData?.users.map((user) => [user.user_guid, user]))
    const historyIncomplete = Boolean(priceData?.incomplete || (coreData && (
        coreData.coverage.unclassified_messages_records || coreData.coverage.unclassified_documents_records
    )))
    const analysisIncomplete = Boolean(analyzerData?.coverage.undated_analyses)
    const firstMonth = [...availableMonths, currentMonth].filter((value) => /^\d{4}-(0[1-9]|1[0-2])$/.test(value)).sort()[0]
    const selectedYear = month.slice(0, 4)
    const years = Array.from({length: Number(currentMonth.slice(0, 4)) - Number(firstMonth.slice(0, 4)) + 1}, (_, index) => Number(currentMonth.slice(0, 4)) - index)
    const months = MONTH_NAMES.map((name, index) => ({name, value: String(index + 1).padStart(2, '0')}))
        .filter(({value}) => `${selectedYear}-${value}` >= firstMonth && `${selectedYear}-${value}` <= currentMonth)

    function changeYear(year: string): void {
        const desired = `${year}-${month.slice(5)}`
        setMonth(desired < firstMonth ? firstMonth : desired > currentMonth ? currentMonth : desired)
    }

    return (
        <Box component="section" aria-labelledby="monthly-usage-title" sx={{mb: 4, minWidth: 0}}>
            <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} justifyContent="space-between" alignItems={{xs: 'stretch', sm: 'center'}} sx={{mb: 2}}>
                <Typography id="monthly-usage-title" variant="h6" component="h2">Utilização por competência</Typography>
                <Stack direction="row" spacing={2}>
                    <TextField select label="Mês" value={month.slice(5)} onChange={(event) => setMonth(`${selectedYear}-${event.target.value}`)} SelectProps={{native: true}} size="small" sx={{minWidth: 150}}>
                        {months.map(({name, value}) => <option key={value} value={value}>{name}</option>)}
                    </TextField>
                    <TextField select label="Ano" value={selectedYear} onChange={(event) => changeYear(event.target.value)} SelectProps={{native: true}} size="small" sx={{minWidth: 100}}>
                        {years.map((year) => <option key={year} value={year}>{year}</option>)}
                    </TextField>
                </Stack>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                Consumo individual da organização no mês selecionado, pelo horário de Brasília.
                Análise de Documentos mostra análises no mês da conclusão registrada.
                As demais funcionalidades mostram créditos registrados no mês, descontados os estornos identificados.
            </Typography>
            {failed && (
                <Alert severity="warning" action={<Button color="inherit" onClick={() => setRetry((value) => value + 1)}>Tentar novamente</Button>} sx={{mb: 2}}>
                    Não foi possível carregar {[
                        status(core) === 'error' && 'Consultivo, Documentos, Pesquisa de Preços, Radar de Fornecedores e Ata Inteligente',
                        status(analyzer) === 'error' && 'Análise de Documentos',
                        status(core) !== 'error' && status(price) === 'error' && 'Pesquisa de Preços e Radar de Fornecedores',
                    ].filter(Boolean).join('; ')}. Valores indisponíveis aparecem como “—”.
                </Alert>
            )}
            {authDenied && (
                <Alert severity="info" action={<Button color="inherit" onClick={onLogin}>Entrar novamente</Button>} sx={{mb: 2}}>
                    Acesso ao relatório não autorizado. Entre novamente para atualizar suas permissões.
                </Alert>
            )}
            {historyIncomplete && (
                <Alert severity="info" sx={{mb: 2}}>
                    Parte do histórico não identifica a funcionalidade utilizada. Células afetadas mostram o consumo identificado como “parcial”, ou “—” quando não há total conhecido.
                </Alert>
            )}
            {analysisIncomplete && (
                <Alert severity="info" sx={{mb: 2}}>
                    Análises antigas sem data de conclusão não podem ser atribuídas a um mês. Células afetadas mostram apenas as conclusões datadas, ou “—” quando não há total conhecido.
                </Alert>
            )}
            {loading && <LinearProgress aria-label="Carregando utilização mensal" />}
            <TableContainer tabIndex={0} role="region" aria-label="Utilização mensal por usuário" sx={{border: 1, borderColor: 'divider', borderRadius: 2, overflowX: 'auto'}}>
                <Table size="small" aria-label="Consumo por usuário e funcionalidade" aria-busy={loading} sx={{minWidth: 1100}}>
                    <TableHead sx={{backgroundColor: 'action.hover'}}>
                        <TableRow>
                            <TableCell>Usuário</TableCell>
                            <TableCell align="right"><ColumnLabel label="Consultivo" description={UNITS.consultivo} /></TableCell>
                            <TableCell align="right"><ColumnLabel label="Documentos" description={UNITS.documentos} /></TableCell>
                            <TableCell align="right"><ColumnLabel label="Análise de Documentos" description={UNITS.analise} /></TableCell>
                            <TableCell align="right"><ColumnLabel label="Pesquisa de Preços" description={UNITS.pesquisa} /></TableCell>
                            <TableCell align="right"><ColumnLabel label="Radar de Fornecedores" description={UNITS.radar} /></TableCell>
                            <TableCell align="right"><ColumnLabel label="Ata Inteligente" description={UNITS.ata} /></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {users.map((user) => {
                            const credits = coreUsers.get(user.user_guid)
                            const analysis = analyzerUsers.get(user.user_guid)
                            const research = priceData?.users.get(user.user_guid)
                            const messagesPartial = credits?.unclassified_messages_records
                                ? 'Existem consultas históricas sem identificação da funcionalidade. Total pode estar incompleto.' : undefined
                            const documentsPartial = credits?.unclassified_documents_records
                                ? 'Existem documentos históricos sem identificação da funcionalidade. Total pode estar incompleto.' : undefined
                            const pricePartial = research?.incomplete
                                ? 'Parte do consumo histórico não identifica se pertence a Pesquisa de Preços ou Radar de Fornecedores. Total pode estar incompleto.'
                                : messagesPartial
                            const analysisPartial = analysis?.undated_analyses
                                ? 'Existem análises antigas sem data de conclusão. Somente as conclusões datadas podem ser atribuídas a este mês.' : undefined
                            return (
                                <TableRow key={user.user_guid}>
                                    <TableCell component="th" scope="row">
                                        <Typography variant="body2">{`${user.first_name} ${user.last_name}`.trim() || user.email}</Typography>
                                        <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                                        {user.org_admin && <Chip label="Administrador" size="small" sx={{ml: 1}} />}
                                        {!user.is_active && <Chip label="Inativo" size="small" sx={{ml: 1}} />}
                                    </TableCell>
                                    <TableCell align="right"><UsageValue value={credits?.consultivo} partial={messagesPartial} /></TableCell>
                                    <TableCell align="right"><UsageValue value={credits?.documentos} partial={documentsPartial} /></TableCell>
                                    <TableCell align="right"><UsageValue value={analyzerData ? analysis?.analise_documentos ?? 0 : undefined} partial={analysisPartial} /></TableCell>
                                    <TableCell align="right"><UsageValue value={priceData ? research?.pesquisa_precos ?? 0 : undefined} partial={pricePartial} /></TableCell>
                                    <TableCell align="right"><UsageValue value={priceData ? research?.radar_fornecedores ?? 0 : undefined} partial={pricePartial} /></TableCell>
                                    <TableCell align="right"><UsageValue value={credits?.ata_inteligente} partial={documentsPartial} /></TableCell>
                                </TableRow>
                            )
                        })}
                        {!users.length && (
                            <TableRow><TableCell colSpan={7} align="center">
                                {loading ? 'Carregando usuários…' : failed ? 'Usuários indisponíveis. Tente novamente.' : 'Nenhum usuário vinculado à organização.'}
                            </TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <Typography variant="caption" color="text.secondary" component="p" sx={{mt: 1}}>
                Análise de Documentos considera registros ainda disponíveis e com vínculo verificável com a organização.
            </Typography>
        </Box>
    )
}
